import CustomBreadcrumb from '@/components/custom-breadcrumb/custom-breadcrumb';
import { SurveysHeader } from '@/components/surveys-header/surveys-header';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs/use-breadcrumbs';
import { Flex } from 'antd';
import styles from './surveys.module.scss';
import { SurveysTable } from '@/components/surveys-table/surveys-table';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import {
  DEFAULT_PAGINATION,
  useSortAndPagination,
} from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import { useSurveys } from '@/hooks/use-surveys/use-surveys';
import { useEffect, useState } from 'react';
import { SurveysFilters } from '@/services/surveys-service/surveys-service.types';
import { useUser } from '@/hooks/use-user/use-user';
import { useCompanyStore } from '@/store/app.store';
import { DEFAULT_TIME_RANGE } from '../conversations/conversations.constans';
import { Dayjs } from 'dayjs';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useExportSurveysHistory } from '@/hooks/use-export-surveys-history/use-export-surveys-history';

export function Surveys() {
  const items = useBreadcrumbs();
  const { companyName, setCompanyName } = useCompanyStore();
  const { userData } = useUser();
  const isCompanyAdmin = userData?.roles.find((role) => role.name === 'company_admin');
  const { companies } = useCompaniesList();
  const hasMultipleCompanies = (companies?.length ?? 0) > 1;
  const isAdmin = isCompanyAdmin && !!userData?.default_company;

  const [surveysFilters, setSurveysFilters] = useState<SurveysFilters>({
    time_range: DEFAULT_TIME_RANGE,
    order_by: 'desc',
    page: DEFAULT_PAGINATION.page,
    items_per_page: DEFAULT_PAGINATION.items_per_page,
    company_name: companyName || (isCompanyAdmin ? userData?.default_company : userData?.company?.name),
    start_time: undefined,
    end_time: undefined,
  });

  useEffect(() => {
    if (companyName) {
      setSurveysFilters((prev) => ({
        ...prev,
        company_name: companyName,
      }));
    } else if (isCompanyAdmin && userData?.default_company) {
      setSurveysFilters((prev) => ({
        ...prev,
        company_name: userData.default_company,
      }));
    }
  }, [companyName, isCompanyAdmin, userData?.default_company]);

  const { exportSurveysHistory, exportContextHolder } = useExportSurveysHistory({
    params: surveysFilters,
  });

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { paginationDetails, sortDetails, onPaginationChanged, onSortChange } =
    useSortAndPagination({
      defaultSorting: { sort_by: 'created_at', order_by: 'desc' },
    });

  const handleChange = (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => {
    onPaginationChanged(
      page || DEFAULT_PAGINATION.page,
      perPage || DEFAULT_PAGINATION.items_per_page
    );
    onSortChange(sortBy || 'created_at', orderBy || 'desc');
  };

  const {
    surveysData,
    refetch: refetchSurveys,
    isPending,
  } = useSurveys({
    filtersParams: {
      ...surveysFilters,
      company_name: surveysFilters.company_name,
      time_zone: timeZone,
    },
    paginationParams: { ...paginationDetails, ...sortDetails },
  });

  const handleRefreshData = () => {
    refetchSurveys();
  };

  const handleExportHistory = async () => {
    await exportSurveysHistory();
  };

  const handleCompanyChange = (filters: SurveysFilters) => {
    setSurveysFilters(filters);
    setCompanyName(filters.company_name ?? undefined);
  };

  const onClearDateRangeFilter = () => {
    setSurveysFilters((prevFilters) => ({
      ...prevFilters,
      time_range: '7d',
      start_time: undefined,
      end_time: undefined,
    }));
    setDates(null);
  };

  const onChangeDateRangeFilter = (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => {
    setSurveysFilters((prevFilters) => ({
      ...prevFilters,
      time_range: undefined,
      start_time: filterValue.time_range.start_time,
      end_time: filterValue.time_range.end_time,
    }));
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  return (
    <Flex vertical className={styles.container}>
      <CustomBreadcrumb items={items} />

      <div>
        <div>
          {exportContextHolder && <div key="export-message-holder">{exportContextHolder}</div>}
        </div>

        <SurveysHeader
          onSearchQueryChange={setCompanySearchQuery}
          searchResults={companySearchData}
          isSearchLoading={companySearchLoading}
          searchError={companySearchError}
          averageScore={surveysData?.avarage_score || 0}
          surveysNumber={surveysData?.surveys_count || 0}
          onRefreshData={handleRefreshData}
          filters={surveysFilters}
          onChangeFilters={handleCompanyChange}
          companiesList={companies?.map((company) => company.name) || []}
          dates={dates}
          setDates={setDates}
          onDateRangeClear={onClearDateRangeFilter}
          onChangeDateRangeFilter={onChangeDateRangeFilter}
          onExportHistory={handleExportHistory}
          isCompanySelectDisabled={!!isCompanyAdmin && !hasMultipleCompanies}
        />
        <SurveysTable
          currentPage={paginationDetails.page}
          onChange={handleChange}
          surveysData={surveysData?.surveys}
          isAdmin={isAdmin}
          pagination={surveysData?.pagination}
          loading={isPending}
        />
      </div>
    </Flex>
  );
}
